import { useRef } from "react";
import { Bold, Italic, Underline } from "lucide-react";

/**
 * Renders text with simple markdown-like formatting:
 * **bold**, *italic*, __underline__
 */
export function FormattedText({ text, className = "" }) {
  if (!text) return null;

  // Parse and render formatted segments
  const parts = [];
  let remaining = text;
  let key = 0;

  const patterns = [
    { regex: /\*\*(.+?)\*\*/g, render: (m) => <strong key={key++}>{m}</strong> },
    { regex: /\*(.+?)\*/g, render: (m) => <em key={key++}>{m}</em> },
    { regex: /__(.+?)__/g, render: (m) => <u key={key++}>{m}</u> },
  ];

  // Simple approach: process all patterns in order
  // First, split by **bold**, then *italic*, then __underline__
  const processSegment = (segment, patternIndex) => {
    if (patternIndex >= patterns.length) {
      return [segment];
    }

    const { regex, render } = patterns[patternIndex];
    regex.lastIndex = 0;
    const result = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(segment)) !== null) {
      if (match.index > lastIndex) {
        result.push(...processSegment(segment.slice(lastIndex, match.index), patternIndex + 1));
      }
      result.push(render(match[1]));
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < segment.length) {
      result.push(...processSegment(segment.slice(lastIndex), patternIndex + 1));
    }

    return result;
  };

  const rendered = processSegment(remaining, 0);

  return <span className={className}>{rendered}</span>;
}

/**
 * Formatting toolbar for a textarea. Wraps selected text with formatting markers.
 */
export function FormattingToolbar({ textareaRef, value, onChange }) {
  const wrapSelection = (prefix, suffix) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = value.substring(start, end);

    // If text is selected, wrap it. Otherwise insert markers with cursor between them.
    const newText =
      value.substring(0, start) +
      prefix +
      (selected || "text") +
      suffix +
      value.substring(end);

    onChange(newText);

    // Restore cursor position
    requestAnimationFrame(() => {
      textarea.focus();
      if (selected) {
        textarea.selectionStart = start;
        textarea.selectionEnd = end + prefix.length + suffix.length;
      } else {
        textarea.selectionStart = start + prefix.length;
        textarea.selectionEnd = start + prefix.length + 4; // select "text"
      }
    });
  };

  return (
    <div className="flex items-center gap-1 mb-1">
      <button
        type="button"
        onClick={() => wrapSelection("**", "**")}
        className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
        title="Bold (**text**)"
        data-testid="format-bold-btn"
      >
        <Bold className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => wrapSelection("*", "*")}
        className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
        title="Italic (*text*)"
        data-testid="format-italic-btn"
      >
        <Italic className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => wrapSelection("__", "__")}
        className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
        title="Underline (__text__)"
        data-testid="format-underline-btn"
      >
        <Underline className="w-3.5 h-3.5" />
      </button>
      <span className="text-white/20 text-xs ml-2">Use **bold**, *italic*, __underline__</span>
    </div>
  );
}
